import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { device } from '@jam3/detect';
import classnames from 'classnames';

import styles from './ShareLinks.module.scss';

import BaseButton from '@/components/BaseButton/BaseButton';
import BaseLink, { Target } from '@/components/BaseLink/BaseLink';
import ShareButton from '@/components/ShareButton/ShareButton';

import Clipboard from '@/components/svgs/Clipboard.svg';
import Facebook from '@/components/svgs/Facebook.svg';
import Linkedin from '@/components/svgs/Linkedin.svg';
import Twitter from '@/components/svgs/Twitter.svg';
import { shareCopy } from '@/data/data';

export type Props = {
  className?: string;
  title: string;
  share: {
    title: string;
    description: string;
    src: string;
    fileName: string;
  };
  href: string;
};

function ShareLinks({ className, title, share, href }: Props) {
  const [showCopiedLink, setShowCopiedLink] = useState(false);
  const [canShare, setCanShare] = useState<boolean>(false);
  const timeout = useRef<NodeJS.Timeout | null>();

  useEffect(() => {
    setCanShare(
      (device.mobile || device.tablet) &&
        typeof window.navigator !== 'undefined' &&
        typeof window.navigator?.canShare !== 'undefined'
    );
    return () => {
      timeout.current && clearTimeout(timeout.current);
    };
  }, []);

  const copyToClipboard = useCallback((text: string) => {
    setShowCopiedLink(true);
    timeout.current = setTimeout(() => {
      setShowCopiedLink(false);
    }, 2000);
    navigator.clipboard.writeText(decodeURIComponent(text));
  }, []);

  const getShareUrls = useCallback(
    (title: string) => {
      const url = encodeURIComponent(href);
      const encodedTitle = encodeURIComponent(title);

      return {
        linkedIn: {
          url: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
          icon: <Linkedin />,
          title: shareCopy.linkedin,
          copyLink: false
        },
        twitter: {
          url: `https://twitter.com/share?url=${url}&text=${encodedTitle}`,
          icon: <Twitter />,
          title: shareCopy.twitter,
          copyLink: false
        },
        facebook: {
          url: `https://facebook.com/sharer.php?u=${url}`,
          icon: <Facebook />,
          title: shareCopy.facebook,
          copyLink: false
        },
        copy: {
          url: url,
          icon: <Clipboard />,
          title: shareCopy.copy,
          copyLink: true
        }
      };
    },
    [href]
  );

  const shareUrls = useMemo(() => getShareUrls(title), [title, getShareUrls]);

  return (
    <div className={classnames(styles.ShareLinks, className)}>
      <div className={styles.label}>{title}</div>
      {canShare ? (
        <ShareButton className={styles.shareButton} {...share} title={title} canShare={canShare} />
      ) : (
        <div className={styles.socialMedia}>
          {Object.values(shareUrls).map((shareData, index) => {
            if (
              shareData.copyLink &&
              typeof window !== 'undefined' &&
              typeof window.navigator.clipboard !== 'undefined'
            ) {
              return (
                <BaseButton className={styles.clipboardContainer} onClick={() => copyToClipboard(href)} key={index}>
                  <div className={classnames(styles.copiedText, { [styles.showCopied]: showCopiedLink })}>
                    {shareCopy.copied}
                  </div>
                  <Clipboard className={styles.clipboard} />
                </BaseButton>
              );
            } else if (!canShare) {
              return (
                <BaseLink
                  aria-label={`Share this page on ${shareData.title}.`}
                  key={index}
                  href={shareData.url}
                  rel="noopener noreferrer"
                  target={Target.BLANK}
                  className={styles.socialIcon}
                >
                  <span className={classnames(styles.iconInner)}>{shareData.icon}</span>
                </BaseLink>
              );
            }
            return null;
          })}
        </div>
      )}
    </div>
  );
}

export default memo(ShareLinks);
